// InlineImagePrompt — a small, in-chat super-credit popup that appears
// right below the most recent assistant reply when the model detected a
// visual intent (e.g. "show me a block diagram of X"). Replaces the
// previous full-screen modal for the post-reply offer flow.
//
// Design intent:
//   • lives inside the chat scroller, NOT a modal — keeps the student in
//     the conversation flow.
//   • compact: kind chip + 1-line prompt preview + Generate / Skip.
//   • on Generate → button shows a spinner; once the image bubble lands
//     above, this card vanishes (parent unmounts it).
//   • aesthetically matches the assistant bubble (same off-white tile)
//     so it reads as part of the message.
import React, { useState } from 'react';
import { Image as ImageIcon, Sparkles, X, Zap } from 'lucide-react';
import { InlineSparkle } from '../../../../components/Loader';

const KIND_META = {
  image:         { label: 'Picture',          tint: '#3B82F6' },
  flowchart:     { label: 'Flowchart',        tint: '#8B5CF6' },
  block_diagram: { label: 'Block diagram',    tint: '#0EA5E9' },
  mindmap:       { label: 'Mind map',         tint: '#22C55E' },
  chart:         { label: 'Chart',            tint: '#F59E0B' },
  sketch:        { label: 'Sketch',           tint: '#EC4899' },
  photo:         { label: 'Photo',            tint: '#10B981' },
};

export default function InlineImagePrompt({
  offer,                // { kind, suggested_prompt, flash_cost, pro_cost, balance }
  onGenerate,           // (prompt, kind, quality) => Promise — parent unmounts us on success
  onSkip,
}) {
  const [busy, setBusy] = useState(false);
  const [quality, setQuality] = useState('flash');

  if (!offer) return null;
  const meta = KIND_META[offer.kind] || KIND_META.image;
  const cost = quality === 'flash' ? offer.flash_cost : offer.pro_cost;
  const canAfford = (offer.balance ?? 0) >= cost;

  const handleGenerate = async () => {
    if (busy || !canAfford) return;
    setBusy(true);
    try {
      await onGenerate(offer.suggested_prompt || '', offer.kind || 'image', quality);
    } finally {
      // If the parent didn't unmount us (e.g. failure path) re-enable.
      setBusy(false);
    }
  };

  return (
    <div className="flex justify-start" data-testid="inline-image-prompt">
      <div
        className="max-w-[85%] rounded-2xl px-3 py-2.5 flex flex-col gap-2"
        style={{
          background: `color-mix(in srgb, ${meta.tint} 10%, var(--st-card, #fff))`,
          border: `1.5px solid color-mix(in srgb, ${meta.tint} 45%, transparent)`,
          color: 'var(--st-ink, #0f172a)',
        }}>
        {/* Header line: kind + close */}
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center text-white shrink-0"
            style={{ background: meta.tint }}>
            <ImageIcon className="w-3 h-3" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold leading-tight">
              Generate as {meta.label.toLowerCase()}?
            </div>
            <div className="text-xs opacity-70 truncate" title={offer.suggested_prompt}>
              {offer.suggested_prompt || 'this concept'}
            </div>
          </div>
          <button
            onClick={onSkip}
            disabled={busy}
            data-testid="inline-image-skip"
            title="Skip"
            className="w-5 h-5 rounded-md flex items-center justify-center hover:bg-black/10 disabled:opacity-40 shrink-0">
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* Quality + Generate row */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setQuality('flash')}
            disabled={busy}
            data-testid="inline-image-flash"
            className="px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 transition"
            style={{
              background: quality === 'flash' ? meta.tint : 'transparent',
              color: quality === 'flash' ? '#fff' : 'inherit',
              border: `1px solid ${quality === 'flash' ? meta.tint : 'var(--st-border, #e5e7eb)'}`,
            }}>
            <Zap className="w-2.5 h-2.5" /> Flash · {offer.flash_cost}cr
          </button>
          <button
            onClick={() => setQuality('pro')}
            disabled={busy}
            data-testid="inline-image-pro"
            className="px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 transition"
            style={{
              background: quality === 'pro' ? meta.tint : 'transparent',
              color: quality === 'pro' ? '#fff' : 'inherit',
              border: `1px solid ${quality === 'pro' ? meta.tint : 'var(--st-border, #e5e7eb)'}`,
            }}>
            <Sparkles className="w-2.5 h-2.5" /> Pro · {offer.pro_cost}cr
          </button>
          <div className="flex-1" />
          <button
            onClick={handleGenerate}
            disabled={busy || !canAfford}
            data-testid="inline-image-generate"
            className="px-3 py-1.5 rounded-full text-xs font-semibold text-white inline-flex items-center gap-1.5 transition disabled:opacity-40 disabled:translate-y-0"
            style={{ background: meta.tint }}>
            {busy ? (
              <InlineSparkle size={14} label="Generating image" />
            ) : (
              <>Generate · {cost}cr</>
            )}
          </button>
        </div>

        {/* Balance line — small, only shows when low or unaffordable */}
        {!canAfford && (
          <div
            className="text-xs font-semibold px-2 py-1 rounded"
            style={{ background: 'color-mix(in srgb, #ef4444 14%, transparent)', color: '#b91c1c' }}
            data-testid="inline-image-shortfall">
            Need {cost - (offer.balance ?? 0)} more credits. Tap the credits pill to top up.
          </div>
        )}
      </div>
    </div>
  );
}
